import React, { useReducer, useContext, useEffect, useCallback } from 'react';
import {
	editModifierRestaurantPropertiesInitialState,
	EditModifierRestaurantPropertiesContext,
	TTypeOfModalWindow,
} from './editModifierRestaurantPropertiesContext';
import { IAppContext, AppContext } from 'appState/appContext';
import { editModifierRestaurantPropertiesReducer } from './editModifierRestaurantPropertiesReducer';
import { EditModifierRestaurantPropertiesActionTypes } from './editModifierRestaurantPropertiesActions';
import {
	UserRoles,
	IGetModifierInfoV2,
	IGetModifierRestaurantPropertiesArguments,
	IGetModifierRestaurantProperties,
	IUpdateModifierRestaurantProperties,
	IRestaurantPropertiesModifierGroup,
	IServerError,
	ICircularReferenceValidationError,
	TPortionSelectionMode,
} from 'types';
import { defaultPortionSelectionMode } from 'appConstants/dishes';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { useFetchData } from 'hooks/useFetchData';
import { getModifierInfoV2, getModifierRestaurantProperties } from 'backend/modifiers';

export const EditModifierRestaurantPropertiesState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(
		editModifierRestaurantPropertiesReducer,
		editModifierRestaurantPropertiesInitialState,
	);
	const { showLoader, hideLoader, loggedUser } = useContext<IAppContext>(AppContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);

	const params = new URL(document.location.href).searchParams;
	const modifierID = params.get('modifierID');

	const { response: modifierInfoResponse, loading: modifierInfoLoading } = useFetchData<IGetModifierInfoV2, string>(
		modifierID ? getModifierInfoV2 : null,
		modifierID || '',
	);

	const {
		response: restaurantPropertiesResponse,
		loading: restaurantPropertiesLoading,
		refetch: restaurantPropertiesRefetch,
	} = useFetchData<IGetModifierRestaurantProperties, IGetModifierRestaurantPropertiesArguments>(
		state.IDSelectedRestaurant ? getModifierRestaurantProperties : null,
		{
			restaurantId: state.IDSelectedRestaurant || '',
			modifierId: state.modifierId,
		},
	);

	useEffect(() => {
		modifierInfoLoading || restaurantPropertiesLoading ? showLoader() : hideLoader();
	}, [modifierInfoLoading, restaurantPropertiesLoading]);

	useEffect(() => {
		if (modifierInfoResponse) {
			const userRestaurantIDs = loggedUser.restaurants
				? loggedUser.restaurants.map((restaurant) => restaurant.id)
				: [];

			const fetchedRestaurants = hasAccess
				? modifierInfoResponse.data.restaurants
				: modifierInfoResponse.data.restaurants.filter((restaurant) =>
						userRestaurantIDs.includes(restaurant.id),
				  );

			dispatch({
				type: EditModifierRestaurantPropertiesActionTypes.SET_ALL_DATA,
				payload: {
					modifierId: modifierInfoResponse.data.id,
					modifierName: modifierInfoResponse.data.name || '',
					customerName: modifierInfoResponse.data.customer.name || '',
					hasSchedulePriority: modifierInfoResponse.data.overrideScheduleAvailability,
					fetchedRestaurants,
				},
			});
		}
	}, [modifierID, loggedUser.restaurants, hasAccess, modifierInfoResponse]);

	useEffect(() => {
		if (restaurantPropertiesResponse) {
			dispatch({
				type: EditModifierRestaurantPropertiesActionTypes.SET_DATA_FROM_RESTAURANT,
				payload: {
					price: restaurantPropertiesResponse.data.price,
					supplementPrice: restaurantPropertiesResponse.data.supplementPrice,
					isCreatedFromPos: restaurantPropertiesResponse.data.isCreatedFromPos,
					quantityMaxLimit: restaurantPropertiesResponse.data.quantityMaxLimit,
					inStop: restaurantPropertiesResponse.data.isDisabled,
					isRestricted: restaurantPropertiesResponse.data.isRestricted,
					isPreselected: restaurantPropertiesResponse.data.isPreselected,
					forceDisable: restaurantPropertiesResponse.data.forceDisable,
					activationSchedule: restaurantPropertiesResponse.data.activationCron || '',
					deactivationSchedule: restaurantPropertiesResponse.data.deactivationCron || '',
					parentModifierGroups: restaurantPropertiesResponse.data.parentModifierGroups,
					IDsSelectedModifierGroups: restaurantPropertiesResponse.data.childModifierGroups.map(
						(modifierGroup) => modifierGroup.id,
					),
					modifierGroupsWithAdditionalData: restaurantPropertiesResponse.data.childModifierGroups,
					hasPortions: restaurantPropertiesResponse.data.hasPortions,
					portionSelectionMode: restaurantPropertiesResponse.data.portionSelectionMode,
					hasPortionBasedNestedModGroups: restaurantPropertiesResponse.data.hasPortionBasedNestedModGroups,
				},
			});
		}
	}, [restaurantPropertiesResponse]);

	useEffect(() => {
		if (state.needRefetchRestPropsData) {
			restaurantPropertiesRefetch();
			dispatch({
				type: EditModifierRestaurantPropertiesActionTypes.REFETCH_REST_PROPS_DATA,
				payload: {
					needRefetchRestPropsData: false,
				},
			});
		}
	}, [state.needRefetchRestPropsData]);

	useEffect(() => {
		restaurantPropertiesRefetch();
	}, [state.IDSelectedRestaurant]);

	const refetchRestPropsData = useCallback((value: boolean) => {
		dispatch({
			type: EditModifierRestaurantPropertiesActionTypes.REFETCH_REST_PROPS_DATA,
			payload: {
				needRefetchRestPropsData: value,
			},
		});
	}, []);

	const setIDRestaurant = useCallback(
		(value: Nullable<string>) => {
			dispatch({
				type: EditModifierRestaurantPropertiesActionTypes.SET_ID_RESTAURANT,
				payload: {
					IDSelectedRestaurant: value,
					price: value ? state.price : null,
					supplementPrice: value ? state.supplementPrice : null,
					isCreatedFromPos: value ? state.isCreatedFromPos : false,
					quantityMaxLimit: value ? state.quantityMaxLimit : null,
					inStop: value ? state.inStop : false,
					isRestricted: value ? state.isRestricted : false,
					isPreselected: value ? state.isPreselected : false,
					forceDisable: value ? state.forceDisable : false,
					activationSchedule: value ? state.activationSchedule : '',
					deactivationSchedule: value ? state.deactivationSchedule : '',
					parentModifierGroups: value ? state.parentModifierGroups : [],
					IDsSelectedModifierGroups: value ? state.IDsSelectedModifierGroups : [],
					modifierGroupsWithAdditionalData: value ? state.modifierGroupsWithAdditionalData : [],
					hasPortions: value ? state.hasPortions : false,
					portionSelectionMode: value ? state.portionSelectionMode : defaultPortionSelectionMode,
					hasPortionBasedNestedModGroups: value ? state.hasPortionBasedNestedModGroups : false,
				},
			});
		},
		[
			state.price,
			state.supplementPrice,
			state.quantityMaxLimit,
			state.inStop,
			state.isRestricted,
			state.isPreselected,
			state.forceDisable,
			state.activationSchedule,
			state.deactivationSchedule,
			state.IDsSelectedModifierGroups,
			state.modifierGroupsWithAdditionalData,
			state.isCreatedFromPos,
			state.hasPortions,
			state.portionSelectionMode,
			state.hasPortionBasedNestedModGroups,
		],
	);

	const changePrice = useCallback((value: Nullable<number>) => {
		dispatch({
			type: EditModifierRestaurantPropertiesActionTypes.CHANGE_PRICE,
			payload: {
				price: value,
			},
		});
	}, []);

	const changeSupplementPrice = useCallback((value: Nullable<number>) => {
		dispatch({
			type: EditModifierRestaurantPropertiesActionTypes.CHANGE_SUPPLEMENT_PRICE,
			payload: {
				supplementPrice: value,
			},
		});
	}, []);

	const changeQuantityMaxLimit = useCallback((value: Nullable<number>) => {
		dispatch({
			type: EditModifierRestaurantPropertiesActionTypes.CHANGE_QUANTITY_MAX_LIMIT,
			payload: {
				quantityMaxLimit: value,
			},
		});
	}, []);

	const toggleInStop = useCallback(() => {
		dispatch({
			type: EditModifierRestaurantPropertiesActionTypes.TOGGLE_IN_STOP,
		});
	}, []);

	const toggleIsRestricted = useCallback(() => {
		dispatch({
			type: EditModifierRestaurantPropertiesActionTypes.TOGGLE_IS_RESTRICTED,
		});
	}, []);

	const toggleIsPreselected = useCallback(() => {
		dispatch({
			type: EditModifierRestaurantPropertiesActionTypes.TOGGLE_IS_PRESELECTED,
		});
	}, []);

	const toggleForceDisable = useCallback(() => {
		dispatch({
			type: EditModifierRestaurantPropertiesActionTypes.TOGGLE_FORCE_DISABLE,
		});
	}, []);

	const changePortionSelectionMode = useCallback((value: TPortionSelectionMode) => {
		dispatch({
			type: EditModifierRestaurantPropertiesActionTypes.CHANGE_PORTION_SELECTION_MODE,
			payload: {
				portionSelectionMode: value,
			},
		});
	}, []);

	const changeActivationSchedule = useCallback((value: string) => {
		dispatch({
			type: EditModifierRestaurantPropertiesActionTypes.CHANGE_ACTIVATION_SCHEDULE,
			payload: {
				activationSchedule: value,
			},
		});
	}, []);

	const changeDeactivationSchedule = useCallback((value: string) => {
		dispatch({
			type: EditModifierRestaurantPropertiesActionTypes.CHANGE_DEACTIVATION_SCHEDULE,
			payload: {
				deactivationSchedule: value,
			},
		});
	}, []);

	const setModifierGroups = useCallback((value: string[]) => {
		dispatch({
			type: EditModifierRestaurantPropertiesActionTypes.SET_MODIFIER_GROUP,
			payload: {
				IDsSelectedModifierGroups: value,
			},
		});
	}, []);

	const setModifierGroupsWithAdditionalData = useCallback((value: IRestaurantPropertiesModifierGroup[]) => {
		dispatch({
			type: EditModifierRestaurantPropertiesActionTypes.SET_MODIFIER_GROUP_WITH_ADDITIONAL_DATA,
			payload: {
				modifierGroupsWithAdditionalData: value,
			},
		});
	}, []);

	const setDataForSubmit = useCallback((value: Nullable<IUpdateModifierRestaurantProperties>) => {
		dispatch({
			type: EditModifierRestaurantPropertiesActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: EditModifierRestaurantPropertiesActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const showErrorModalWithInfo = useCallback((value: Nullable<IServerError<ICircularReferenceValidationError>>) => {
		dispatch({
			type: EditModifierRestaurantPropertiesActionTypes.SHOW_ERROR_MODAL_WITH_INFO,
			payload: {
				errorResponseData: value,
			},
		});
	}, []);

	return (
		<EditModifierRestaurantPropertiesContext.Provider
			value={{
				refetchRestPropsData,
				setIDRestaurant,
				changePrice,
				changeSupplementPrice,
				changeQuantityMaxLimit,
				toggleInStop,
				toggleIsRestricted,
				toggleIsPreselected,
				toggleForceDisable,
				changePortionSelectionMode,
				changeActivationSchedule,
				changeDeactivationSchedule,
				setModifierGroups,
				setModifierGroupsWithAdditionalData,
				setDataForSubmit,
				changeTypeOfModalWindow,
				showErrorModalWithInfo,
				needRefetchRestPropsData: state.needRefetchRestPropsData,
				customerName: state.customerName,
				fetchedRestaurants: state.fetchedRestaurants,
				IDSelectedRestaurant: state.IDSelectedRestaurant,
				modifierId: state.modifierId,
				modifierName: state.modifierName,
				price: state.price,
				supplementPrice: state.supplementPrice,
				isCreatedFromPos: state.isCreatedFromPos,
				quantityMaxLimit: state.quantityMaxLimit,
				inStop: state.inStop,
				isRestricted: state.isRestricted,
				isPreselected: state.isPreselected,
				forceDisable: state.forceDisable,
				hasSchedulePriority: state.hasSchedulePriority,
				activationSchedule: state.activationSchedule,
				deactivationSchedule: state.deactivationSchedule,
				parentModifierGroups: state.parentModifierGroups,
				IDsSelectedModifierGroups: state.IDsSelectedModifierGroups,
				modifierGroupsWithAdditionalData: state.modifierGroupsWithAdditionalData,
				hasPortions: state.hasPortions,
				portionSelectionMode: state.portionSelectionMode,
				hasPortionBasedNestedModGroups: state.hasPortionBasedNestedModGroups,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
				errorResponseData: state.errorResponseData,
			}}>
			{children}
		</EditModifierRestaurantPropertiesContext.Provider>
	);
};
